import React, { useMemo } from "react";
import { AbsoluteFill, useCurrentFrame } from "remotion";
import type { SurveyVideoProps } from "../../lib/schema";
import type { StatData, CityBubble, TrailPoint } from "../../types/stats";
import {
  frameToYear,
  getAmount,
  getInterpolatedAmount,
} from "../../lib/animation";
import { getCityColorMap, TEXT_PRIMARY, TEXT_SECONDARY } from "../../lib/colors";
import { computeScale, dataToSvg, PLOT_LAYOUT } from "../../lib/scale";
import { resolveCities } from "../../lib/selection";
import GridLines from "../Graph/GridLines";
import Axis from "../Graph/Axis";
import Trail from "../Graph/Trail";
import BubblePlot from "../Graph/BubblePlot";
import YearCounter from "../Graph/YearCounter";

type MainGraphProps = Pick<SurveyVideoProps, "itemX" | "itemY" | "cities" | "outlierSigma"> & {
  data: StatData;
  globalStartFrame: number;
};

const MainGraph: React.FC<MainGraphProps> = ({
  itemX,
  itemY,
  cities,
  data,
  globalStartFrame,
  outlierSigma,
}) => {
  const localFrame = useCurrentFrame();
  const absoluteFrame = localFrame + globalStartFrame;
  // アニメーションは 780f で 2025 年に到達し、残り 90f (3s) は停止
  const currentYear = frameToYear(absoluteFrame, 150, 780);

  // ── 都市リスト解決 ──────────────────────────────────
  const allCities = useMemo(
    () => resolveCities(cities, data.cities),
    [cities, data.cities],
  );
  const many = allCities.length > 3;

  // ── カラーマップ ────────────────────────────────────
  const colorMap = useMemo(
    () => getCityColorMap(allCities),
    [allCities],
  );

  // ── 軸スケール (全年・全都市のデータ範囲で固定) ─────
  const { xScale, yScale } = useMemo(() => {
    const xValues: number[] = [];
    const yValues: number[] = [];

    for (const year of data.years) {
      for (const city of allCities) {
        const xv = getAmount(data, year, city, itemX);
        const yv = getAmount(data, year, city, itemY);
        if (xv !== null) xValues.push(xv);
        if (yv !== null) yValues.push(yv);
      }
    }

    return {
      xScale: computeScale(xValues),
      yScale: computeScale(yValues),
    };
  }, [data, allCities, itemX, itemY]);

  // ── レイアウト定数 ──────────────────────────────────
  const { left, top, plotWidth, plotHeight } = PLOT_LAYOUT;

  // ── バブル座標計算 ──────────────────────────────────
  const bubbles: CityBubble[] = [];
  for (const city of allCities) {
    const xv = getInterpolatedAmount(data, currentYear, city, itemX);
    const yv = getInterpolatedAmount(data, currentYear, city, itemY);
    if (xv === null || yv === null) continue;

    bubbles.push({
      city,
      x: dataToSvg(xv, xScale, left, left + plotWidth),
      y: dataToSvg(yv, yScale, top + plotHeight, top),
    });
  }

  // ── 外れ値検出 (σ 閾値は Props で可変) ──────────────
  const outlierCities = useMemo(() => {
    // σ=0 の場合は全都市にラベルを表示
    if (outlierSigma === 0) {
      return new Set(bubbles.map((b) => b.city));
    }
    if (bubbles.length < 4) return new Set<string>();

    const xs = bubbles.map((b) => b.x);
    const ys = bubbles.map((b) => b.y);
    const meanX = xs.reduce((a, b) => a + b, 0) / xs.length;
    const meanY = ys.reduce((a, b) => a + b, 0) / ys.length;
    const stdX = Math.sqrt(
      xs.reduce((s, v) => s + (v - meanX) ** 2, 0) / xs.length,
    );
    const stdY = Math.sqrt(
      ys.reduce((s, v) => s + (v - meanY) ** 2, 0) / ys.length,
    );

    const outliers = new Set<string>();
    for (const b of bubbles) {
      if (
        Math.abs(b.x - meanX) > stdX * outlierSigma ||
        Math.abs(b.y - meanY) > stdY * outlierSigma
      ) {
        outliers.add(b.city);
      }
    }
    return outliers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [absoluteFrame, allCities.length, outlierSigma]);

  // ── 軌跡データ (過去の整数年) ──────────────────────
  const endYearFloor = Math.floor(currentYear);
  const trails = useMemo(() => {
    const trailMap = new Map<string, TrailPoint[]>();

    for (const city of allCities) {
      const points: TrailPoint[] = [];
      for (const year of data.years) {
        if (year > endYearFloor) break;
        const xv = getAmount(data, year, city, itemX);
        const yv = getAmount(data, year, city, itemY);
        if (xv === null || yv === null) continue;

        points.push({
          x: dataToSvg(xv, xScale, left, left + plotWidth),
          y: dataToSvg(yv, yScale, top + plotHeight, top),
        });
      }
      if (points.length > 0) {
        trailMap.set(city, points);
      }
    }
    return trailMap;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endYearFloor, allCities.length]);

  return (
    <AbsoluteFill
      style={{
        fontFamily: "'Segoe UI', Meiryo, 'Noto Sans JP', sans-serif",
      }}
    >
      <svg viewBox="0 0 1920 1080" style={{ width: "100%", height: "100%" }}>
        <GridLines xScale={xScale} yScale={yScale} />
        <Axis
          xScale={xScale}
          yScale={yScale}
          labelX={itemX}
          labelY={itemY}
        />
        <Trail trails={trails} colorMap={colorMap} />
        <BubblePlot
          bubbles={bubbles}
          colorMap={colorMap}
          outlierCities={outlierCities}
          many={many}
        />
      </svg>

      {/* HTML オーバーレイ */}
      <YearCounter year={currentYear} />
      <div
        style={{
          position: "absolute",
          top: 24,
          right: 32,
          fontFamily: "'Segoe UI', Meiryo, 'Noto Sans JP', sans-serif",
          pointerEvents: "none",
          textAlign: "right",
        }}
      >
        <div style={{ fontSize: 42, color: TEXT_SECONDARY, opacity: 0.8 }}>
          （出典）総務省「家計調査（二人以上の世帯）」 支出金額
        </div>
        <div style={{ fontSize: 40, color: TEXT_PRIMARY, marginTop: 4 }}>
          {itemX} × {itemY}
        </div>
      </div>
    </AbsoluteFill>
  );
};

export default MainGraph;
